/**
 * ⚠️ このファイルは今後使用しません
 *
 * カラー定義は以下に移行しました：
 * packages/shared/tailwind/tokens.ts
 *
 */
/**
 * 汎用カラーパレット
 *
 * テーマカラー（brand, success, warning, error）とは別に、
 * イベント・スタンプ・シフトパターン・休日種類など
 * ユーザーが選択可能な色として使用する。
 *
 * 6行 x 5列 = 30色
 * - 暖色系（濃 / 中 / 淡）
 * - 寒色系（濃 / 中 / 淡）
 */

// ---------------------------------------------------------------------------
// 型定義
// ---------------------------------------------------------------------------

/** パレットカラー1色分の定義 */
export interface PaletteColor {
    /** 一意キー（英名） */
    key: string;
    /** 表示名（和名） */
    label: string;
    /** HEXカラーコード（#RRGGBB） */
    hex: string;
    /** 色の濃さ */
    intensity: "vivid" | "medium" | "light";
    /** 色温度 */
    tone: "warm" | "cool";
}

// ---------------------------------------------------------------------------
// パレット定義
// ---------------------------------------------------------------------------

/** 暖色系 — 濃い色 */
const WARM_VIVID: PaletteColor[] = [
    { key: "leafGreen",       label: "リーフグリーン",       hex: "#a0ba4d", intensity: "vivid", tone: "warm" },
    { key: "karashiiro",      label: "からしいろ",           hex: "#caae41", intensity: "vivid", tone: "warm" },
    { key: "sancha",          label: "さんちゃ",             hex: "#dd7d15", intensity: "vivid", tone: "warm" },
    { key: "begonia",         label: "ベゴニア",             hex: "#ef6b6a", intensity: "vivid", tone: "warm" },
    { key: "cosmos",          label: "コスモス",             hex: "#e26e9f", intensity: "vivid", tone: "warm" },
];

/** 暖色系 — 中間色 */
const WARM_MEDIUM: PaletteColor[] = [
    { key: "chartreuseGreen", label: "シャトルーズグリーン", hex: "#c0d37e", intensity: "medium", tone: "warm" },
    { key: "kihadairo",       label: "きはだ色",             hex: "#dfca77", intensity: "medium", tone: "warm" },
    { key: "goldenYellow",    label: "ゴールデンイエロー",   hex: "#f39b4f", intensity: "medium", tone: "warm" },
    { key: "shrimpPink",      label: "シュリンプピンク",     hex: "#ff8f8a", intensity: "medium", tone: "warm" },
    { key: "rosePink",        label: "ローズピンク",         hex: "#f491b6", intensity: "medium", tone: "warm" },
];

/** 暖色系 — 淡い色 */
const WARM_LIGHT: PaletteColor[] = [
    { key: "limeYellow",      label: "ライムイエロー",       hex: "#e0ecac", intensity: "light", tone: "warm" },
    { key: "leghorn",         label: "レグホーン",           hex: "#f5e6a9", intensity: "light", tone: "warm" },
    { key: "yuuou",           label: "ゆうおう",             hex: "#ffd9a7", intensity: "light", tone: "warm" },
    { key: "shellPink",       label: "シェルピンク",         hex: "#ffd3cb", intensity: "light", tone: "warm" },
    { key: "sakurairo",       label: "桜色",                 hex: "#ffd4e8", intensity: "light", tone: "warm" },
];

/** 寒色系 — 濃い色 */
const COOL_VIVID: PaletteColor[] = [
    { key: "ayameiro",        label: "あやめ色",             hex: "#bc79df", intensity: "vivid", tone: "cool" },
    { key: "cyan",            label: "シアン",               hex: "#1798fb", intensity: "vivid", tone: "cool" },
    { key: "turquoiseBlue",   label: "ターコイズブルー",     hex: "#3cbedb", intensity: "vivid", tone: "cool" },
    { key: "nileBlue",        label: "ナイルブルー",         hex: "#29c4b6", intensity: "vivid", tone: "cool" },
    { key: "wakatakeiro",     label: "わかたけ色",           hex: "#54c494", intensity: "vivid", tone: "cool" },
];

/** 寒色系 — 中間色 */
const COOL_MEDIUM: PaletteColor[] = [
    { key: "lilac",           label: "ライラック",           hex: "#ca9ee7", intensity: "medium", tone: "cool" },
    { key: "wasurenagusa",    label: "勿忘草色",             hex: "#78b2f9", intensity: "medium", tone: "cool" },
    { key: "horizonBlue",     label: "ホライゾンブルー",     hex: "#80d7eb", intensity: "medium", tone: "cool" },
    { key: "iceGreen",        label: "アイスグリーン",       hex: "#79dbcd", intensity: "medium", tone: "cool" },
    { key: "mintGreen",       label: "ミントグリーン",       hex: "#87dcb2", intensity: "medium", tone: "cool" },
];

/** 寒色系 — 淡い色 */
const COOL_LIGHT: PaletteColor[] = [
    { key: "lavender",        label: "ラベンダー",           hex: "#d3c5df", intensity: "light", tone: "cool" },
    { key: "lightBlue",       label: "ライトブルー",         hex: "#bacce5", intensity: "light", tone: "cool" },
    { key: "babyBlue",        label: "ベビーブルー",         hex: "#b4effd", intensity: "light", tone: "cool" },
    { key: "hisoku",          label: "ひそく",               hex: "#aef3e6", intensity: "light", tone: "cool" },
    { key: "skyGreen",        label: "スカイグリーン",       hex: "#b8f3d2", intensity: "light", tone: "cool" },
];

// ---------------------------------------------------------------------------
// エクスポート — グループ別
// ---------------------------------------------------------------------------

/** 行ごとにグループ化されたパレット（UIでの行表示に便利） */
export const PALETTE_ROWS = [
    WARM_VIVID,
    WARM_MEDIUM,
    WARM_LIGHT,
    COOL_VIVID,
    COOL_MEDIUM,
    COOL_LIGHT,
] as const;

// ---------------------------------------------------------------------------
// エクスポート — フラット配列 & マップ
// ---------------------------------------------------------------------------

/** 全30色のフラット配列 */
export const PALETTE_COLORS: PaletteColor[] = PALETTE_ROWS.flat();

/** key → PaletteColor のマップ（O(1)参照用） */
export const PALETTE_MAP: Record<string, PaletteColor> = Object.fromEntries(
    PALETTE_COLORS.map((c) => [c.key, c]),
);

/** key → HEXコード のマップ（O(1)参照用） */
export const PALETTE_HEX_MAP: Record<string, string> = Object.fromEntries(
    PALETTE_COLORS.map((c) => [c.key, c.hex]),
);

// ---------------------------------------------------------------------------
// 型 — パレットキーのユニオン型
// ---------------------------------------------------------------------------

export type PaletteColorKey = typeof PALETTE_COLORS[number]["key"];

// ---------------------------------------------------------------------------
// ユーティリティ
// ---------------------------------------------------------------------------

/**
 * パレットキーまたはHEXコードからHEXコードを取得する。
 * - パレットキーの場合 → 対応するHEXを返す
 * - #で始まるHEXの場合 → そのまま返す
 * - 該当なし → fallback（デフォルト: ベゴニア #ef6b6a）
 */
export const resolveHex = (colorKeyOrHex: string, fallback = "#ef6b6a"): string => {
    if (colorKeyOrHex.startsWith("#")) return colorKeyOrHex;
    return PALETTE_HEX_MAP[colorKeyOrHex] ?? fallback;
};

/**
 * HEXカラーコードが明るい色かどうかを判定する。
 * テキスト色の自動決定（明るい背景 → 暗いテキスト）に使用。
 */
export const isLightColor = (hexColor: string): boolean => {
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6;
};

/**
 * 背景色に適したテキスト色を返す。
 * - 明るい背景 → "text-gray-800"
 * - 暗い背景 → "text-white"
 */
export const getTextColorClass = (hexColor: string): string => {
    return isLightColor(hexColor) ? "text-gray-800" : "text-white";
};
